import { c as _c } from "react/compiler-runtime";
import { Box, Button, Center, chakra, Heading, Link, Text, VStack, Dialog, Portal } from '@chakra-ui/react';
import { CloseButton } from '@src/components/chakra-ui/close-button';
import { CompanyProfileForm } from '@src/components/sco/Account/CompanyProfileForm';
import { UserExperiencesEditor } from '@src/components/sco/Account/UserExperiencesEditor';
import { UserGoalsForm } from '@src/components/sco/Account/UserGoalsForm';
import { UserLoginForm } from '@src/components/sco/Account/UserLoginForm';
import { UserProfileForm } from '@src/components/sco/Account/UserProfileForm';
import { UserSignupProfileForm } from '@src/components/sco/Account/UserSignupProfileForm';
import { UserSignupRegisterForm } from '@src/components/sco/Account/UserSignupRegisterForm';
import { UserSkillsEditor } from '@src/components/sco/Account/UserSkillsEditor';
import { ButtonLink } from '@src/components/sco/ButtonLink.tsx';
import { ConversionCheckout } from '@src/components/sco/Conversion/controller/ConversionCheckout';
import { ConversionPlans } from '@src/components/sco/Conversion/controller/ConversionPlans';
import { ADVISOR_TEAM_PRODUCT_ID, STARTUPS_PRO_PRODUCT_ID, type PaywallIntentId } from '@src/components/sco/Conversion/intent';
import { FormContainer } from '@src/components/sco/FormContainer';
import { SupportOptionsBlock } from '@src/components/sco/SupportOptionsBlock';
import { CheckCircleIcon, ChevronRightIcon } from '@src/components/sco/icons';
import { BreakpointContextProvider } from '@src/components/sco/utilities/BreakpointProvider';
import { SSRGate } from '@src/components/sco/utilities/SSRGate';
import { useCurrentUser } from '@src/hooks/useCurrentUser';
import { clearDialogBodyLocks } from '@src/lib/clearDialogBodyLocks';
import { omit } from 'lodash';
import { useSearchParams } from 'next/navigation';
import { parseAsString, useQueryState, useQueryStates } from 'nuqs';
import React, { createContext, ReactElement, ReactNode, useContext, useEffect, useLayoutEffect, useState } from 'react';
import ConfettiExplosion from 'react-confetti-explosion';
type PromptQueryParams = {
  productId?: string | null;
  termId?: string | null;
  couponCode?: string | null;
};

/** Default layout styles for Dialog.Content (matches pre-refactor PromptContent). */
const defaultDialogContentProps = {
  bgColor: 'colorPalette.container',
  width: 'full',
  maxWidth: '32em'
} as const;
type DialogContentPropsMap = Record<string, unknown>;
type SetDialogContentProps = (props: DialogContentPropsMap) => void;

// PromptContent pushes per-prompt width/bg styles up onto the stable Dialog.Content
// shell without remounting it (focus-trap stays bound to that node).
const PromptDialogContentPropsContext = createContext<SetDialogContentProps | null>(null);
const releaseDialogBodyLocks = () => {
  // Zag applies body pointer-events via queueMicrotask; clear after paint so we
  // win any race against a late lock apply on a torn-down dialog.
  requestAnimationFrame(() => {
    clearDialogBodyLocks();
  });
};
export const PromptHandler = () => {
  const $ = _c(27);
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = {
      prompt: parseAsString,
      productId: parseAsString,
      termId: parseAsString,
      couponCode: parseAsString,
      intent: parseAsString,
      tokens: parseAsString
    };
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  const [t1, setQueryStates] = useQueryStates(t0);
  const {
    prompt: promptKey
  } = t1;
  const [renderedPromptKey, setRenderedPromptKey] = useState(null);
  const [dialogContentProps, setDialogContentProps] = useState(defaultDialogContentProps);
  let t2;
  let t3;
  if ($[1] !== promptKey) {
    t2 = () => {
      if (promptKey && prompts[promptKey]) {
        setRenderedPromptKey(promptKey);
      }
    };
    t3 = [promptKey];
    $[1] = promptKey;
    $[2] = t2;
    $[3] = t3;
  } else {
    t2 = $[2];
    t3 = $[3];
  }
  useEffect(t2, t3);
  const isOpen = !!(promptKey && prompts[promptKey]);
  const activePromptKey = promptKey && prompts[promptKey] && promptKey || renderedPromptKey || null;
  const activePromptConfig = activePromptKey && prompts[activePromptKey] || undefined;
  const ActivePrompt = activePromptConfig?.render;
  let t4;
  if ($[4] !== setQueryStates) {
    t4 = () => {
      setQueryStates({
        prompt: null,
        intent: null,
        tokens: null
      });
      releaseDialogBodyLocks();
    };
    $[4] = setQueryStates;
    $[5] = t4;
  } else {
    t4 = $[5];
  }
  const onClose = t4;
  let t5;
  if ($[6] !== setQueryStates) {
    t5 = (prompt, params) => {
      setQueryStates({
        prompt,
        productId: params?.productId ?? null,
        termId: params?.termId ?? null,
        couponCode: params?.couponCode ?? null
      });
    };
    $[6] = setQueryStates;
    $[7] = t5;
  } else {
    t5 = $[7];
  }
  const setPromptKey = t5;
  let t6;
  if ($[8] !== onClose) {
    t6 = t7 => {
      const {
        open
      } = t7;
      if (!open) {
        onClose();
      }
    };
    $[8] = onClose;
    $[9] = t6;
  } else {
    t6 = $[9];
  }
  const handleOpenChange = t6;
  let t7;
  if ($[10] === Symbol.for("react.memo_cache_sentinel")) {
    t7 = () => {
      setRenderedPromptKey(null);
      setDialogContentProps(defaultDialogContentProps);
      clearDialogBodyLocks();
    };
    $[10] = t7;
  } else {
    t7 = $[10];
  }
  const handleExitComplete = t7;
  let t8;
  if ($[11] !== activePromptConfig?.modalProps) {
    t8 = activePromptConfig?.modalProps || {};
    $[11] = activePromptConfig?.modalProps;
    $[12] = t8;
  } else {
    t8 = $[12];
  }
  let t9;
  if ($[13] === Symbol.for("react.memo_cache_sentinel")) {
    t9 = <Dialog.Backdrop backdropFilter="blur(4px)" />;
    $[13] = t9;
  } else {
    t9 = $[13];
  }
  let t10;
  if ($[14] !== ActivePrompt || $[15] !== activePromptKey || $[16] !== dialogContentProps || $[17] !== onClose || $[18] !== setPromptKey) {
    t10 = ActivePrompt && activePromptKey && <PromptDialogContentPropsContext.Provider value={setDialogContentProps}><Dialog.Content {...defaultDialogContentProps} {...dialogContentProps}><ActivePrompt key={activePromptKey} setPromptKey={setPromptKey} onClose={onClose} /></Dialog.Content></PromptDialogContentPropsContext.Provider>;
    $[14] = ActivePrompt;
    $[15] = activePromptKey;
    $[16] = dialogContentProps;
    $[17] = onClose;
    $[18] = setPromptKey;
    $[19] = t10;
  } else {
    t10 = $[19];
  }
  let t11;
  if ($[20] !== t10) {
    t11 = <Dialog.Positioner>{t10}</Dialog.Positioner>;
    $[20] = t10;
    $[21] = t11;
  } else {
    t11 = $[21];
  }
  let t12;
  if ($[22] !== handleOpenChange || $[23] !== isOpen || $[24] !== t11 || $[25] !== t8) {
    t12 = <Portal><Dialog.Root open={isOpen} onExitComplete={handleExitComplete} onOpenChange={handleOpenChange} {...t8}>{t9}{t11}</Dialog.Root></Portal>;
    $[22] = handleOpenChange;
    $[23] = isOpen;
    $[24] = t11;
    $[25] = t8;
    $[26] = t12;
  } else {
    t12 = $[26];
  }
  return t12;
};
type PromptRenderProps = {
  onClose: () => any;
  setPromptKey: (promptKey: string, otherParams?: PromptQueryParams) => any;
};
type PromptConfig = {
  render: (props: PromptRenderProps) => ReactElement;
  /** Props forwarded to Dialog.Root (Chakra v3). Do not use v2 Modal props. */
  modalProps?: Omit<Dialog.RootProps, 'open' | 'onOpenChange' | 'onExitComplete' | 'children'>;
};

/**
 * Registers layout props onto the stable Dialog.Content shell, then renders
 * the prompt body. Must NOT render Dialog.Content itself (focus-trap).
 */
const PromptContent = (props: {
  children?: ReactNode;
  onClose: any;
  [key: string]: any;
}) => {
  const setDialogContentProps = useContext(PromptDialogContentPropsContext);
  const contentStyleProps = omit(props, ['setPromptKey', 'onClose', 'children']);
  useLayoutEffect(() => {
    setDialogContentProps?.({
      ...defaultDialogContentProps,
      ...contentStyleProps
    });
    // Prompt bodies remount per activePromptKey; styles are fixed for that mount.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  return <>
      {props.onClose && <Dialog.CloseTrigger asChild insetEnd={2} position={'absolute'} top={2} zIndex={1}>
          <CloseButton color={'colorPalette.fg/50'} size={'sm'} _hover={{
        color: 'white',
        bgColor: 'whiteAlpha.200'
      }} />
        </Dialog.CloseTrigger>}
      <BreakpointContextProvider>{props.children}</BreakpointContextProvider>
    </>;
};
function conversionCheckoutPrompt(defaults: {
  defaultProductId?: string;
  defaultIntent?: PaywallIntentId;
} = {}): PromptConfig {
  return {
    render: (props: PromptRenderProps) => <PromptContent {...props} bgColor={'colorPalette.container'} maxWidth={'min(60rem, calc(100vw - 2rem))'} padding={0} width={'auto'}>
        <ConversionCheckout {...defaults} onClose={props.onClose} />
      </PromptContent>
  };
}
function conversionPlansPrompt(defaults: {
  defaultIntent?: PaywallIntentId;
} = {}): PromptConfig {
  return {
    render: (props: PromptRenderProps) => <PromptContent {...props} bgColor={'colorPalette.container'} maxWidth={'min(60rem, calc(100vw - 2rem))'} padding={0} width={'auto'}>
        <ConversionPlans {...defaults} />
      </PromptContent>
  };
}
const prompts: Record<string, PromptConfig> = {
  test: {
    render: (props: PromptRenderProps) => {
      return <PromptContent {...props}>
          <Heading>Test Prompt</Heading>
        </PromptContent>;
    }
  },
  consultation: {
    render: (props: PromptRenderProps) => {
      return <PromptContent maxWidth={'container.md'} padding={6} {...props}>
          <chakra.iframe borderRadius={'md'} minHeight={'660px'} src={'https://meetings.hubspot.com/joe-bonaccorso/fg-meeting?embed=true'} width={'100%'} />
          <Box paddingY={'1em'}>
            <Button width={'full'} onClick={() => props.onClose()}>
              Close
            </Button>
          </Box>
        </PromptContent>;
    }
  },
  register: {
    render: (props: PromptRenderProps) => {
      const {
        currentUser
      } = useCurrentUser();
      // Prefill from interactive chat: nuqs `email` (same source ScoQuestion sets)
      const [emailFromNuqs] = useQueryState('email', parseAsString);
      const searchParams = useSearchParams();
      const emailPrefill = emailFromNuqs || searchParams?.get('email') || undefined;
      return <PromptContent {...props}>
          <UserSignupRegisterForm containerProps={{
          padding: ['1.5em', '1.5em', '2em', '2.5em']
        }} emailField={emailPrefill ? {
          autoFocus: false
        } : undefined} title={currentUser?.isRegistered ? 'Setup Your Account' : 'Create an Account'} afterSuccess={() => {
          return props.onClose();
        }} onLoginClick={() => props.setPromptKey('login')} defaultValues={emailPrefill ? {
          email: emailPrefill
        } : undefined}
        // Auto-focus name when email already filled from chat
        nameField={emailPrefill ? {
          autoFocus: true
        } : undefined} />
        </PromptContent>;
    }
  },
  'user-edit-profile': {
    render: (props: PromptRenderProps) => {
      return <PromptContent maxWidth={'container.md'} {...props}>
          <UserProfileForm afterSuccess={() => props.onClose()} />
        </PromptContent>;
    }
  },
  'user-edit-startup': {
    render: (props: PromptRenderProps) => {
      return <PromptContent maxWidth={'container.md'} {...props}>
          <CompanyProfileForm afterSuccess={() => props.onClose()} />
        </PromptContent>;
    }
  },
  'user-edit-experiences': {
    render: (props: PromptRenderProps) => {
      return <PromptContent maxWidth={'container.md'} {...props}>
          <UserExperiencesEditor />
        </PromptContent>;
    }
  },
  'user-edit-skills': {
    render: (props: PromptRenderProps) => {
      return <PromptContent maxWidth={'container.md'} {...props}>
          <UserSkillsEditor />
        </PromptContent>;
    }
  },
  'register-profile': {
    render: (props: PromptRenderProps) => {
      return <PromptContent {...props}>
          <UserSignupProfileForm containerProps={{
          padding: ['1em', '1em', '2em', '3em']
        }} title={'What are you working on?'} afterSuccess={() => {
          props.onClose();
        }} />
        </PromptContent>;
    }
  },
  login: {
    render: (props: PromptRenderProps) => {
      return <PromptContent {...props}>
          <UserLoginForm containerProps={{
          padding: ['1em', '1em', '2em', '3em']
        }} title={'Login'} afterSuccess={() => {
          props.onClose();
        }} onRegisterClick={() => props.setPromptKey('register')} />
        </PromptContent>;
    }
  },
  /** Conversion checkout: sells exactly `productId` (purchase, or a quoted prorated change). */
  /** Conversion checkout: sells exactly `productId` (purchase, or a quoted prorated change). */
  checkout: conversionCheckoutPrompt(),
  /** Plan chooser on the Conversion kit; choosing a plan swaps to `checkout`. */
  plans: conversionPlansPrompt(),
  // Legacy commerce keys stay as aliases so email, CMS, and ad links keep working.
  subscribe: conversionCheckoutPrompt({
    defaultProductId: STARTUPS_PRO_PRODUCT_ID
  }),
  'subscribe-lite': conversionCheckoutPrompt({
    defaultProductId: STARTUPS_PRO_PRODUCT_ID
  }),
  upgrade: conversionCheckoutPrompt({
    defaultProductId: ADVISOR_TEAM_PRODUCT_ID
  }),
  signup: conversionCheckoutPrompt({
    defaultProductId: STARTUPS_PRO_PRODUCT_ID
  }),
  overlay: conversionCheckoutPrompt({
    defaultProductId: STARTUPS_PRO_PRODUCT_ID,
    defaultIntent: 'investor-search'
  }),
  topics: {
    render: (props: PromptRenderProps) => {
      const {
        currentUser
      } = useCurrentUser();
      const [method, setMethod] = useState('register');
      if (currentUser?.isRegistered) {
        return <PromptContent {...props} maxWidth={'45em'}>
            <UserGoalsForm afterSuccess={() => props.onClose()} hideFounderNeedsOther={true} />
          </PromptContent>;
      } else {
        return <PromptContent {...props}>
            {method == 'register' ? <UserSignupRegisterForm returnToHref={null} title={'Join to Edit Your Dashboard'} onLoginClick={() => setMethod('login')} /> : <UserLoginForm returnToHref={null} title={'Login'} onRegisterClick={() => setMethod('register')} />}
          </PromptContent>;
      }
    }
  },
  welcome: {
    render: (props: PromptRenderProps) => {
      return <PromptContent maxWidth={'full'} width={'container.sm'} {...props}>
          <Center>
            <SSRGate disableSSR={true}>
              <ConfettiExplosion duration={3000} particleSize={6} zIndex={10000} />
            </SSRGate>
          </Center>
          <FormContainer height={'full'}>
            <VStack align={'center'} gap={'1.5em'} height={'full'} justify={'center'} marginY={'1em'}>
              <CheckCircleIcon fontSize={'2em'} opacity={0.5} />
              <Heading>Welcome!</Heading>
              <Text>Your membership has been updated.</Text>
              <Box textAlign={'center'} width={'full'}>
                <ButtonLink href={'/onboarding/welcome'} size={'lg'} target={'_blank'} variant={'cta'}>
                  Setup Your Membership
                  <ChevronRightIcon />
                </ButtonLink>

                <Text marginTop={'1em'}>
                  {'Or'}{' '}
                  <Link variant={'underline'} onClick={() => props.onClose()}>
                    close
                  </Link>{' '}
                  to explore on your own.
                </Text>
              </Box>
            </VStack>
          </FormContainer>
        </PromptContent>;
    }
  },
  'investor-matching-plans': conversionPlansPrompt({
    defaultIntent: 'investor-outreach'
  }),
  support: {
    render: (props: PromptRenderProps) => {
      return <PromptContent {...props} bgColor={'colorPalette.container'} maxWidth={'1000px'}>
          <Box padding={[4, 4, 8]}>
            <Heading marginBottom={2} size={'lg'}>
              We're here to help!
            </Heading>
            <SupportOptionsBlock />
          </Box>
        </PromptContent>;
    }
  },
  'investor-matching-purchase': conversionCheckoutPrompt({
    defaultProductId: STARTUPS_PRO_PRODUCT_ID,
    defaultIntent: 'investor-outreach'
  })
};